import React from "react";
import { fmt, fmtSigned, monthLabel } from "./financeUtils";

/**
 * Dark-green hero card (artifact `.hero`):
 * brand + month nav, AYLIK/YILLIK toggle, big net number,
 * GELİR − GİDER = NET equation, margin chips.
 */
const FinanceHero = ({
  brandText,
  period, // 'monthly' | 'yearly'
  monthDate, // Date (first of selected month)
  onPrev,
  onNext,
  onPeriodChange,
  income,
  expense,
  prevNet, // net of previous period (month/year) or null
  prevLabel, // "geçen aya" | "geçen yıla"
  prevSubLabel, // e.g. "Haziran net: ₺71.000" (or null)
  marginMonthly, // % or null
  marginYearly, // % or null
}) => {
  const net = income - expense;
  const isNeg = net < 0;
  const year = monthDate.getFullYear();

  let delta = null;
  if (prevNet != null && prevNet !== 0) {
    delta = Math.round(((net - prevNet) / Math.abs(prevNet)) * 100);
  }

  return (
    <div className="fin-hero p-[18px] pb-5">
      {/* top row: brand + month nav */}
      <div className="flex justify-between items-center gap-2 flex-wrap mb-3.5">
        <div className="fin-grot flex items-center gap-2 font-semibold text-[13.5px] min-w-0">
          <span className="w-2 h-2 rounded-full bg-[#0f8f6b] shadow-[0_0_0_4px_rgba(15,143,107,0.22)] shrink-0" />
          <span className="truncate">{brandText}</span>
        </div>
        <div className="flex items-center gap-0.5 bg-white/[0.07] border border-white/10 rounded-full px-1 py-[3px]">
          <button
            onClick={onPrev}
            aria-label="Önceki dönem"
            className="text-[#cdd8d3] hover:text-white hover:bg-white/10 text-[15px] px-2 py-0.5 rounded-full transition-colors"
          >
            ‹
          </button>
          <span className="fin-grot font-medium text-[12.5px] min-w-[88px] text-center">
            {period === "yearly" ? `${year} · Yıl` : monthLabel(monthDate)}
          </span>
          <button
            onClick={onNext}
            aria-label="Sonraki dönem"
            className="text-[#cdd8d3] hover:text-white hover:bg-white/10 text-[15px] px-2 py-0.5 rounded-full transition-colors"
          >
            ›
          </button>
        </div>
      </div>

      {/* period toggle */}
      <div className="flex w-max bg-white/[0.07] border border-white/10 rounded-full p-[3px] mb-3.5">
        <button
          onClick={() => onPeriodChange("monthly")}
          className={`fin-grot font-semibold text-[11.5px] tracking-[0.03em] px-4 py-[5px] rounded-full transition-colors ${
            period === "monthly" ? "bg-[#eafff6] text-[#12201b]" : "text-[#a9bcb4]"
          }`}
        >
          AYLIK
        </button>
        <button
          onClick={() => onPeriodChange("yearly")}
          className={`fin-grot font-semibold text-[11.5px] tracking-[0.03em] px-4 py-[5px] rounded-full transition-colors ${
            period === "yearly" ? "bg-[#eafff6] text-[#12201b]" : "text-[#a9bcb4]"
          }`}
        >
          YILLIK · {year}
        </button>
      </div>

      {/* result label + delta chip */}
      <div className="flex items-center gap-2 flex-wrap mb-0.5">
        <span className="text-[10.5px] tracking-[0.15em] uppercase font-semibold text-[#8ba79c]">
          {period === "yearly" ? `${year} Kümülatif · Net Kâr` : "Ay Sonucu · Net Kâr"}
        </span>
        {delta != null && (
          <span
            className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${
              delta >= 0
                ? "bg-[rgba(15,143,107,0.2)] text-[#6ee0b6]"
                : "bg-[rgba(201,70,60,0.2)] text-[#f2a49d]"
            }`}
          >
            {delta >= 0 ? "▲" : "▼"} %{Math.abs(delta)} {prevLabel}
          </span>
        )}
      </div>

      {/* big number */}
      <div
        className={`fin-grot fin-num font-semibold leading-[1.05] tracking-[-0.02em] my-0.5 text-[clamp(40px,13vw,58px)] sm:text-[52px] ${
          isNeg ? "text-[#f5b3ac]" : "text-[#eafff6]"
        }`}
      >
        <span className="text-[0.5em] text-[#8ba79c] font-medium mr-1 align-[0.28em]">₺</span>
        {Math.abs(Math.round(net)).toLocaleString("tr-TR")}
        {isNeg ? " −" : ""}
      </div>
      <div className="text-[12.5px] text-[#a9bcb4]">
        {isNeg
          ? "Dikkat: giderler geliri aştı."
          : period === "yearly"
            ? "Yıl genelinde kârdasın."
            : `Bu ay kârdasın.${prevSubLabel ? ` ${prevSubLabel}` : ""}`}
      </div>

      {/* equation: GELİR − GİDER = NET */}
      <div className="flex gap-1.5 mt-[15px]">
        <div className="flex-1 min-w-0 bg-white/5 border border-white/[0.09] rounded-[11px] px-2.5 py-[9px]">
          <div className="text-[10px] tracking-[0.06em] uppercase text-[#8ba79c] font-semibold mb-0.5">
            Gelir (Ciro)
          </div>
          <div className="fin-grot fin-num font-semibold text-[13px] sm:text-[14.5px] text-[#7fe3bd] truncate">
            {fmt(income)}
          </div>
        </div>
        <div className="flex items-center text-[#5f716b] text-[16px] fin-grot shrink-0">−</div>
        <div className="flex-1 min-w-0 bg-white/5 border border-white/[0.09] rounded-[11px] px-2.5 py-[9px]">
          <div className="text-[10px] tracking-[0.06em] uppercase text-[#8ba79c] font-semibold mb-0.5">
            Gider
          </div>
          <div className="fin-grot fin-num font-semibold text-[13px] sm:text-[14.5px] text-[#f0a69e] truncate">
            {fmt(expense)}
          </div>
        </div>
        <div className="flex items-center text-[#5f716b] text-[16px] fin-grot shrink-0">=</div>
        <div className="flex-1 min-w-0 bg-white/5 border border-white/[0.09] rounded-[11px] px-2.5 py-[9px]">
          <div className="text-[10px] tracking-[0.06em] uppercase text-[#8ba79c] font-semibold mb-0.5">
            Net
          </div>
          <div className="fin-grot fin-num font-semibold text-[13px] sm:text-[14.5px] text-[#eafff6] truncate">
            {fmtSigned(net)}
          </div>
        </div>
      </div>

      {/* margin chips */}
      <div className="flex gap-2 mt-3">
        <div className="flex-1 bg-white/5 border border-white/[0.09] rounded-[10px] px-2.5 py-2 text-[11px] text-[#a9bcb4]">
          Ciro bazlı kârlılık (aylık)
          <b className="fin-grot fin-num block text-[15px] font-semibold text-[#eafff6] mt-px">
            {marginMonthly == null ? "—" : `%${marginMonthly}`}
          </b>
        </div>
        <div className="flex-1 bg-white/5 border border-white/[0.09] rounded-[10px] px-2.5 py-2 text-[11px] text-[#a9bcb4]">
          Ciro bazlı kârlılık (yıllık)
          <b className="fin-grot fin-num block text-[15px] font-semibold text-[#eafff6] mt-px">
            {marginYearly == null ? "—" : `%${marginYearly}`}
          </b>
        </div>
      </div>
    </div>
  );
};

export default FinanceHero;
